import {
  type AgentThreadMessage,
  AgentThreadRole,
  type AgentThreadToolResultContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "@/shared/domain/agentThread/AgentThreadRecord";

export interface MessageSegment {
  type: "messages";
  messages: AgentThreadMessage[];
  messageIndices: number[];
}

export interface ToolGroupSegment {
  type: "toolGroup";
  toolNames: string[];
  toolResults: AgentThreadToolResultContent[];
  totalRuntimeMs: number;
  messageIndices: number[];
}

export type MessageGroup = MessageSegment | ToolGroupSegment;

const hasToolUseContent = (message: AgentThreadMessage): boolean => {
  return (
    message.role === AgentThreadRole.ASSISTANT &&
    message.content.some(isAgentThreadToolUseContent)
  );
};

const hasToolResultContent = (message: AgentThreadMessage): boolean => {
  return (
    message.role === AgentThreadRole.ROBOTO &&
    message.content.some(isAgentThreadToolResultContent)
  );
};

const getNonToolUseMessage = (
  message: AgentThreadMessage,
): AgentThreadMessage | null => {
  const content = message.content.filter(
    (contentItem) => !isAgentThreadToolUseContent(contentItem),
  );

  if (content.length === 0) {
    return null;
  }

  return {
    ...message,
    content,
  };
};

const createToolGroupAtIndex = (
  messages: AgentThreadMessage[],
  startIndex: number,
  isBoundary: (index: number) => boolean,
): {
  nextIndex: number;
  leadingSegments: MessageSegment[];
  segment: ToolGroupSegment;
} | null => {
  const leadingSegments: MessageSegment[] = [];
  const messageIndices: number[] = [];
  const toolResults: AgentThreadToolResultContent[] = [];

  let nextIndex = startIndex;
  let isFirstPair = true;

  while (
    nextIndex + 1 < messages.length &&
    hasToolUseContent(messages[nextIndex]) &&
    hasToolResultContent(messages[nextIndex + 1])
  ) {
    if (!isFirstPair && getNonToolUseMessage(messages[nextIndex]) !== null) {
      break;
    }
    // Never let a tool group straddle a task boundary: a message that starts a
    // new task region opens a fresh segment so the collapsible logic can assign
    // each segment to exactly one task.
    if (!isFirstPair && isBoundary(nextIndex)) {
      break;
    }

    const assistantDisplayMessage = getNonToolUseMessage(messages[nextIndex]);

    if (assistantDisplayMessage) {
      leadingSegments.push({
        type: "messages",
        messages: [assistantDisplayMessage],
        messageIndices: [nextIndex],
      });
    }

    messageIndices.push(nextIndex, nextIndex + 1);
    toolResults.push(
      ...messages[nextIndex + 1].content.filter(isAgentThreadToolResultContent),
    );
    nextIndex += 2;
    isFirstPair = false;
  }

  if (toolResults.length < 1) {
    return null;
  }

  return {
    leadingSegments,
    nextIndex,
    segment: {
      type: "toolGroup",
      toolNames: toolResults.map((toolResult) => toolResult.tool_name),
      toolResults,
      totalRuntimeMs: toolResults.reduce(
        (total, toolResult) => total + toolResult.runtime_ms,
        0,
      ),
      messageIndices,
    },
  };
};

export const groupToolMessages = (
  messages: AgentThreadMessage[],
  isBoundary: (index: number) => boolean = () => false,
): MessageGroup[] => {
  const segments: MessageGroup[] = [];
  const pendingMessages: AgentThreadMessage[] = [];
  const pendingIndices: number[] = [];

  const flushPendingMessages = () => {
    if (pendingMessages.length === 0) {
      return;
    }

    segments.push({
      type: "messages",
      messages: [...pendingMessages],
      messageIndices: [...pendingIndices],
    });

    pendingMessages.length = 0;
    pendingIndices.length = 0;
  };

  let index = 0;

  while (index < messages.length) {
    const toolGroup = createToolGroupAtIndex(messages, index, isBoundary);

    if (toolGroup) {
      flushPendingMessages();
      segments.push(...toolGroup.leadingSegments);
      segments.push(toolGroup.segment);
      index = toolGroup.nextIndex;
      continue;
    }

    // Close the current plain-message segment at a task boundary so it never
    // mixes messages from two tasks.
    if (isBoundary(index)) {
      flushPendingMessages();
    }
    pendingMessages.push(messages[index]);
    pendingIndices.push(index);
    index += 1;
  }

  flushPendingMessages();

  return segments;
};
